import { useEffect, useMemo, useCallback } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';

import { PointOfInterest, useMeasureBlisterLazyQuery } from '@/__generated__/graphql';
import { Graph } from '@/components/Analysis/modules/pointOfInterest/createOrEdit/BlisterMeasurement/Graph';
import { EmptyPlaceholder } from '../styles';

interface Props {
  data: PointOfInterest;
}

export const BlisterMeasurementSection = ({ data }: Props) => {
  const blisterHeight = String(data.blister?.maxHeight)
  const [measureBlister, { data: response, loading }] = useMeasureBlisterLazyQuery({
    fetchPolicy: 'cache-first',
  });

  const performMeasurement = useCallback(async () => {
    if (!data?.blister?.centerPoint || !data?.blister?.radiusPoint || !data?.location?.id) {
      console.error('Missing required blister measurement data');
      return;
    }

    try {
      await measureBlister({
        variables: {
          locationId: data.location.id,
          blisterMeasurementPoints: {
            centerPoint: {
              x: data.blister.centerPoint.bearing,
              y: data.blister.centerPoint.elevation,
            },
            edgePoint: {
              x: data.blister.radiusPoint.bearing,
              y: data.blister.radiusPoint.elevation,
            },
          },
        },
      });
    } catch (err) {
      console.error('Blister measurement error:', err);
    }
  }, [data, measureBlister]);

  useEffect(() => {
    performMeasurement();
  }, [performMeasurement]);

  const { profilePoints, surfacePoints } = useMemo(() => {
    const blister = response?.fetchBlisterHeightMeasurement?.blister;
    return {
      profilePoints: blister?.blisterProfilePlotPoints ?? [],
      surfacePoints: blister?.fittedSurfacePoints ?? [],
    };
  }, [response]);

  if (loading) {
    return (
      <EmptyPlaceholder sx={{ height: '18rem', width: '100%' }}>
        <CircularProgress size={40} />
      </EmptyPlaceholder>
    );
  }

  if (loading && response && !profilePoints.length) {
    return (
      <EmptyPlaceholder sx={{ height: '18rem', width: '100%' }}>
        <Typography variant="body2" color="textSecondary">
          No {data?.type?.toLowerCase()} profile data available.
        </Typography>
      </EmptyPlaceholder>
    );
  }

  return (
    <Box sx={{ height: '18rem', width: '100%' }}>
      <Graph pointsArray={profilePoints} fittedSurfacePointsArray={surfacePoints} viewType='previewTab'
        blisterData={{ estimatedHeight: blisterHeight }}
      />
    </Box>
  );
};
